import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as EmailValidator from 'email-validator';
import FormButton from '@app/component/form/button/FormButton';
import Message from '@app/component/message/Message';
import ReviewsPagePlaceholder from '@app/component/placeholder/ReviewsPagePlaceholder';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import ReportsDeliverySchedule from '@app/model/review/delivery/ReportsDeliverySchedule';
import ReportsDeliveryScheduleState from '@app/model/review/delivery/ReportsDeliveryScheduleState';
import ReportsDeliveryScheduleType from '@app/model/review/delivery/ReportsDeliveryScheduleType';
import WeekDay from '@app/model/review/delivery/WeekDay';
import ReviewsInsightsDeliveryScheduleInput
	from '@app/model/review/delivery/input/ReviewsInsightsDeliveryScheduleInput';
import { ReviewService } from '@app/model/review/service/ReviewService';
import { ReviewsQueryKeys } from '@app/model/review/service/ReviewsQueryKeys';
import { useReviewsTools } from '@app/site/secured/review-manager/provider/useReviewsTools';
import Utilities from '@util/Utilities';
import Switcher from '@app/component/switcher/Switcher';

const ReviewsPropertyInsightsEmailSettings = React.memo(() => {
	const tools = useReviewsTools();

	const { data: schedules, isLoading, isError } = useQuery({
		queryFn: () => ReviewService.findReviewsInsightsDeliverySchedules(tools.filter.companyHotelId),
		queryKey: ReviewsQueryKeys.insightsDeliverySchedules(tools.filter.companyHotelId),
		refetchOnWindowFocus: false,
		refetchOnMount: true,
	});

	if (isLoading)
		return <ReviewsPagePlaceholder />;

	if (isError)
		return <Message state="error">Email settings cannot be loaded at the moment. Please retry again later.</Message>;

	const weeklySchedule = schedules?.find(schedule => schedule.scheduleType === ReportsDeliveryScheduleType.WEEKLY);
	const monthlySchedule = schedules?.find(schedule => schedule.scheduleType === ReportsDeliveryScheduleType.MONTHLY);

	return <div className="space-y-5">
		<div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
			<InsightsDeliveryScheduleCard
				companyHotelId={tools.filter.companyHotelId}
				title="Weekly Insights"
				description="Sent every Monday at 09:00 with the latest weekly digest."
				scheduleType={ReportsDeliveryScheduleType.WEEKLY}
				schedule={weeklySchedule}
			/>
			<InsightsDeliveryScheduleCard
				companyHotelId={tools.filter.companyHotelId}
				title="Monthly Insights"
				description="Sent on the first day of each month at 09:00 with the latest monthly digest."
				scheduleType={ReportsDeliveryScheduleType.MONTHLY}
				schedule={monthlySchedule}
			/>
		</div>
	</div>;
});

type CardState = {
	enabled: boolean;
	recipients: string[];
	email: string;
	error?: string;
}

const InsightsDeliveryScheduleCard: React.FunctionComponent<{
	companyHotelId: number;
	title: string;
	description: string;
	scheduleType: ReportsDeliveryScheduleType;
	schedule?: ReportsDeliverySchedule;
}> = ({ companyHotelId, title, description, scheduleType, schedule }) => {
	const queryClient = useQueryClient();
	const toaster = useToaster();
	const [state, setState] = useState<CardState>(() => initializeState(schedule));

	useEffect(() => {
		setState(initializeState(schedule));
	}, [schedule?.id, schedule?.state, schedule?.recipients?.join('|')]);

	const saveMutation = useMutation({
		mutationFn: (input: ReviewsInsightsDeliveryScheduleInput) => ReviewService.saveReviewsInsightsDeliverySchedule(input),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ReviewsQueryKeys.insightsDeliverySchedules(companyHotelId) });
			toaster.success('Review Insights', `${title} delivery settings saved.`);
		},
		onError: (error: any) => toaster.error('Review Insights', error),
	});

	const addRecipient = () => {
		const email = state.email.trim();

		if (!EmailValidator.validate(email)) {
			setState(current => ({ ...current, error: 'Enter a valid email address.' }));
			return;
		}

		if (state.recipients.includes(email)) {
			setState(current => ({ ...current, email: '', error: undefined }));
			return;
		}

		setState(current => ({
			...current,
			email: '',
			error: undefined,
			recipients: [...current.recipients, email],
		}));
	};

	const removeRecipient = (email: string) => {
		setState(current => ({
			...current,
			recipients: current.recipients.filter(recipient => recipient !== email),
		}));
	};

	const onSave = () => {
		if (state.enabled && state.recipients.length === 0) {
			setState(current => ({ ...current, error: 'Add at least one recipient before enabling this schedule.' }));
			return;
		}

		saveMutation.mutate({
			id: schedule?.id,
			companyHotelId,
			enabled: state.enabled,
			scheduleType,
			recipients: state.recipients,
			time: schedule?.time ?? '09:00',
			weekdays: scheduleType === ReportsDeliveryScheduleType.WEEKLY ? (schedule?.weekdays?.length ? schedule.weekdays : [WeekDay.MONDAY]) : undefined,
			dayOfMonth: scheduleType === ReportsDeliveryScheduleType.MONTHLY ? (schedule?.dayOfMonth ?? 1) : undefined,
		});
	};

	const isActive = state.enabled;
	const hasPersistedActiveSchedule = schedule?.state === ReportsDeliveryScheduleState.ACTIVE;
	const nextDeliveryText = hasPersistedActiveSchedule && schedule?.nextExecutionDate
		? `Next delivery: ${Utilities.formatDateString(schedule.nextExecutionDate, 'MMM DD, yyyy HH:mm')}`
		: 'Next delivery is calculated after saving an active schedule.';

	return <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
		<div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
			<div className="flex items-start justify-between gap-4">
				<div>
					<div className="flex items-center gap-2">
						<h3 className="text-base font-semibold text-slate-950">{title}</h3>
						<span className={isActive
							? 'rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200'
							: 'rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-slate-200'}>
							{isActive ? 'Enabled' : 'Disabled'}
						</span>
					</div>
					<p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
				</div>

				<Switcher
					ariaLabel={`${title} delivery`}
					isLarge
					checked={isActive}
					onChange={(enabled) => setState(current => ({ ...current, enabled, error: undefined }))}
				/>
			</div>
		</div>

		<div className="space-y-5 px-5 py-5">
			<div>
				<div className="flex items-end gap-3">
					<div className="flex-1">
						<label htmlFor={`${scheduleType.toLowerCase()}-recipient`} className="label">Recipient email</label>
						<input id={`${scheduleType.toLowerCase()}-recipient`}
						       type="text"
						       className="input mt-0.5"
						       placeholder="name@company.com"
						       value={state.email}
						       onChange={(event: React.ChangeEvent<HTMLInputElement>) => setState(current => ({
										 ...current,
										 email: event.target.value,
										 error: undefined,
									 }))}
						       onKeyDown={(event) => {
										 if (event.key === 'Enter') {
											 event.preventDefault();
											 addRecipient();
										 }
									 }}
						/>
					</div>
					<FormButton state="white" onClick={addRecipient}>Add</FormButton>
				</div>
				{!!state.error && <p className="mt-2 text-xs text-rose-600">{state.error}</p>}
			</div>

			<div className="min-h-24 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3">
				{state.recipients.length === 0 &&
					<p className="py-6 text-center text-sm text-slate-500">No recipients added for this schedule.</p>}

				{state.recipients.length > 0 &&
					<ul className="flex flex-wrap gap-2">
						{state.recipients.map(email =>
							<li key={email}
							    className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-slate-700 ring-1 ring-slate-200">
								<CheckCircleIcon className="h-4 w-4 text-emerald-500" />
								{email}
								<button type="button" onClick={() => removeRecipient(email)}
								        className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
									<XMarkIcon className="h-4 w-4" />
								</button>
							</li>,
						)}
					</ul>}
			</div>

			<div className="flex items-center justify-between border-t border-slate-100 pt-4">
				<p className="text-xs text-slate-500">
					{nextDeliveryText}
				</p>
				<FormButton state="primary" onClick={onSave} isLoading={saveMutation.isPending}>
					Save
				</FormButton>
			</div>
		</div>
	</section>;
};

const initializeState = (schedule?: ReportsDeliverySchedule): CardState => ({
	enabled: schedule?.state === ReportsDeliveryScheduleState.ACTIVE,
	recipients: schedule?.recipients ?? [],
	email: '',
});

export default ReviewsPropertyInsightsEmailSettings;
